import { create } from 'zustand';

import { INotification, INotificationGroup } from '@/models';

interface NotificationState {
  notifications: INotification[];
  unreadCount: number;
  isPanelOpen: boolean;
  isLoading: boolean;
  dueNotificationRefreshKey: number;

  // Actions
  setNotifications: (notifications: INotification[]) => void;
  addNotification: (notification: INotification) => void;
  dismissNotification: (notificationId: string) => void;
  dismissAllNotifications: () => void;
  togglePanel: () => void;
  setIsPanelOpen: (isOpen: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  updateUnreadCount: () => void;
  getGroupedNotifications: () => INotificationGroup[];
  triggerDueNotificationRefresh: () => void;
}

const initialState = {
  notifications: [],
  unreadCount: 0,
  isPanelOpen: false,
  isLoading: false,
  dueNotificationRefreshKey: 0,
};

export const useNotificationStore = create<NotificationState>((set, get) => ({
  ...initialState,

  setNotifications: (notifications: INotification[]) => {
    set({ notifications });
    get().updateUnreadCount();
  },

  addNotification: (notification: INotification) => {
    set((state) => ({
      notifications: [notification, ...(state.notifications || [])],
    }));
    get().updateUnreadCount();
  },

  dismissNotification: (notificationId: string) => {
    set((state) => ({
      notifications: (state.notifications || []).map((notification) =>
        notification._id === notificationId
          ? { ...notification, isDismissed: true, dismissedAt: new Date() }
          : notification,
      ),
    }));
    get().updateUnreadCount();
  },

  dismissAllNotifications: () => {
    const now = new Date();
    set((state) => ({
      notifications: (state.notifications || []).map((notification) => ({
        ...notification,
        isDismissed: true,
        dismissedAt: now,
      })),
    }));
    get().updateUnreadCount();
  },

  togglePanel: () => {
    set((state) => ({ isPanelOpen: !state.isPanelOpen }));
  },

  setIsPanelOpen: (isOpen: boolean) => {
    set({ isPanelOpen: isOpen });
  },

  setIsLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  updateUnreadCount: () => {
    const { notifications } = get();
    // Safety check: ensure notifications is an array
    if (!notifications || !Array.isArray(notifications)) {
      set({ unreadCount: 0 });
      return;
    }
    const unreadCount = notifications.filter(
      (n) =>
        !n.isDismissed &&
        // The 'deleted' substring filter hides record-deletion leftovers, but
        // skydio messages are verbatim email subjects and must not vanish.
        (n.recordType === 'skydio-email' || !n.message.includes('deleted')),
    ).length;
    set({ unreadCount });
  },

  triggerDueNotificationRefresh: () => {
    set((state) => ({
      dueNotificationRefreshKey: state.dueNotificationRefreshKey + 1,
    }));
  },

  getGroupedNotifications: (): INotificationGroup[] => {
    const { notifications } = get();

    // Safety check: ensure notifications is an array
    if (!notifications || !Array.isArray(notifications)) {
      return [];
    }

    // Filter out dismissed notifications first
    const activeNotifications = notifications.filter(
      (notification) => !notification.isDismissed,
    );

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const todayNotifications = activeNotifications.filter((notification) => {
      const notificationDate = new Date(notification.createdAt);
      return notificationDate >= today;
    });

    const previousNotifications = activeNotifications.filter((notification) => {
      const notificationDate = new Date(notification.createdAt);
      return notificationDate < today;
    });

    const groups: INotificationGroup[] = [];

    if (todayNotifications.length > 0) {
      groups.push({
        title: 'Today',
        notifications: todayNotifications,
      });
    }

    if (previousNotifications.length > 0) {
      groups.push({
        title: 'Previous',
        notifications: previousNotifications,
      });
    }

    return groups;
  },
}));
